import { HugeiconsIcon } from '@hugeicons/react'
import { PlusSignIcon, Target02Icon } from '@hugeicons/core-free-icons'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/shared/layout'
import { HEATMAP_READABLE_CLICKS, formatCount, type HeatmapDevice } from './heatmapInsights'

/**
 * Loading skeleton in the shape of the loaded page (Paper p-L-1, redone for the
 * rail-less layout): top bar controls, answer sentence, capture strip, capture.
 */
export function HeatmapLoading() {
  return (
    <div className="flex min-h-full flex-1 flex-col" aria-busy="true" aria-label="Loading heatmap">
      <div className="flex min-h-[52px] flex-wrap items-center gap-2 border-b border-black/5 px-4 py-[9px] sm:px-5 dark:border-white/[0.07]">
        <Skeleton className="h-8 w-[168px] rounded-lg" />
        <div className="hidden flex-1 sm:block" />
        <Skeleton className="h-[30px] w-[116px] rounded-lg" />
        <Skeleton className="h-[30px] w-[136px] rounded-lg" />
        <Skeleton className="h-8 w-[146px] rounded-lg" />
      </div>
      <div className="flex flex-col gap-2.5 px-4 pb-6 pt-[34px] sm:px-8">
        <Skeleton className="h-[22px] w-full max-w-[440px]" />
        <Skeleton className="h-[13px] w-full max-w-[300px]" />
      </div>
      <div className="flex flex-1 flex-col items-center gap-3 border-t border-black/[0.04] bg-canvas px-4 pb-10 pt-5 sm:px-8 dark:border-white/[0.06]">
        <div className="flex h-4 w-full max-w-[800px] items-center gap-3">
          <Skeleton className="h-3 w-[200px]" />
          <div className="flex-1" />
          <Skeleton className="hidden h-3 w-[240px] sm:block" />
        </div>
        <div className="w-full max-w-[800px] rounded-xl bg-card p-3 shadow-[0_0_0_1px_rgb(9_9_11/0.05)]">
          <Skeleton className="h-[560px] w-full rounded-lg" />
        </div>
      </div>
    </div>
  )
}

/** Paper p-M-1 "Heatmaps · No pages yet". */
export function HeatmapNoPages({ onAddPage }: { onAddPage?: () => void }) {
  return (
    <div className="flex min-h-[780px] flex-1 flex-col items-center bg-canvas px-4 pb-8 pt-24 sm:px-6">
      <div className="w-full max-w-[560px] rounded-xl bg-card shadow-[0_0_0_1px_var(--border)]">
        <EmptyState
          className="px-8 pb-11 pt-12 [&_h3]:mb-1.5 [&_h3]:text-sm [&_h3]:leading-[18px] [&_h3]:tracking-normal [&_p]:mb-0 [&_p]:max-w-[440px] [&_p]:text-[13px] [&_p]:leading-5"
          icon={<HugeiconsIcon icon={Target02Icon} size={18} className="text-muted-foreground/70" />}
          title="No pages on heatmaps yet"
          description="Add a page to see where visitors click and how far they scroll. It is the fastest way to find a button nobody sees."
          action={
            onAddPage ? (
              <Button onClick={onAddPage} className="mt-3 h-8 gap-1.5 px-3 text-[13px]">
                <HugeiconsIcon icon={PlusSignIcon} size={12} strokeWidth={2.4} />
                Add page
              </Button>
            ) : undefined
          }
        />
      </div>
    </div>
  )
}

/** Progress toward a readable map, beside the collecting answer (Paper p-M-1 "Collecting clicks"). */
export function HeatmapCollectingProgress({
  totalClicks,
  device,
  pace,
}: {
  totalClicks: number
  device: HeatmapDevice
  pace: string
}) {
  const ratio = Math.min(1, totalClicks / HEATMAP_READABLE_CLICKS)
  return (
    <div className="flex w-full shrink-0 flex-col gap-2.5 rounded-xl bg-neutral-50 p-3.5 shadow-[0_0_0_1px_var(--border)] sm:w-[300px] dark:bg-white/[0.03]">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-medium leading-4 text-ui-title">
          {device === 'mobile' ? 'Taps on mobile' : 'Clicks on desktop'}
        </span>
        <span className="text-[13px] font-medium leading-4 tabular-nums text-foreground/40">
          {formatCount(totalClicks)} of about {HEATMAP_READABLE_CLICKS}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={HEATMAP_READABLE_CLICKS}
        aria-valuenow={totalClicks}
        aria-label="Clicks collected"
        className="flex h-1.5 rounded-full bg-neutral-200/70 dark:bg-white/10"
      >
        <div className="rounded-full bg-foreground" style={{ width: `${ratio * 100}%` }} />
      </div>
      <p className="text-[12px] leading-4 text-muted-foreground">{pace}</p>
    </div>
  )
}
